import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { X, Mic, Square, Send, Trash2, Lightbulb, Loader2 } from "lucide-react";

const fmtSecs = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function FeatureRequestModal({ onClose }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [sending, setSending] = useState(false);
  const [items, setItems] = useState([]);
  const [secs, setSecs] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/feature-requests"); setItems(data); } catch { /* transient */ }
  };
  useEffect(() => {
    load();
    return () => { clearInterval(timerRef.current); try { recRef.current?.stream?.getTracks()?.forEach((t) => t.stop()); } catch {} };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch {}
        clearInterval(timerRef.current);
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 800) { toast.error("That was too short — tap the mic and talk for a second or two."); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "feature-request.webm");
          const { data } = await api.post("/feature-requests/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) setText((t) => (t ? `${t} ${data.text}` : data.text));
          else toast.info("Couldn't hear anything — try again a bit closer to the mic.");
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Transcription failed — try again.");
        } finally { setTranscribing(false); }
      };
      rec.start();
      recRef.current = rec;
      setSecs(0);
      timerRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
      setRecording(true);
    } catch {
      toast.error("Microphone blocked — allow mic access for this site and try again.");
    }
  };

  const stopRec = () => { try { recRef.current?.stop(); } catch {} };

  const submit = async () => {
    if (!text.trim()) { toast.info("Say or type your idea first."); return; }
    setSending(true);
    try {
      await api.post("/feature-requests", { text: text.trim(), source: "voice" });
      toast.success("Sent — the BotChainCP developer will see it. 💡");
      setText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/feature-requests/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: "rgba(15,18,38,0.55)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: "88vh" }} data-testid="fr-modal">
        <div className="px-6 pt-5 pb-4 flex items-start justify-between border-b" style={{ borderColor: "rgba(15,18,38,0.08)" }}>
          <div>
            <h3 className="font-display text-lg font-bold text-ink flex items-center gap-2"><Lightbulb size={17} style={{ color: "#B45309" }} /> Recommend a feature</h3>
            <p className="text-[13px] text-ink-2 mt-0.5">Tap the mic, describe the feature out loud — it's turned into text and sent to the BotChainCP developer.</p>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink transition-colors" data-testid="fr-close"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          <div className="flex items-start gap-4 mb-4">
            <button
              onClick={recording ? stopRec : startRec}
              disabled={transcribing}
              className="w-16 h-16 rounded-full flex items-center justify-center shrink-0 text-white transition-transform duration-200 hover:scale-105 disabled:opacity-50"
              style={{ background: recording ? "#DC2626" : "#4060FF", boxShadow: recording ? "0 0 0 8px rgba(220,38,38,0.15)" : "0 8px 24px -8px rgba(64,96,255,0.6)" }}
              title={recording ? "Stop recording" : "Start recording"}
              data-testid={recording ? "fr-stop-btn" : "fr-record-btn"}
            >
              {transcribing ? <Loader2 size={22} className="animate-spin" /> : recording ? <Square size={20} /> : <Mic size={24} />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink mb-1" data-testid="fr-rec-status">
                {recording ? <span style={{ color: "#DC2626" }}>● Recording… {fmtSecs(secs)} — tap to stop</span>
                  : transcribing ? "Turning your voice into text…"
                  : "Tap the mic and talk — or just type below."}
              </div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="e.g. “I'd love a weekly email that summarizes the whole team's mining revenue…”"
                className="w-full px-3 py-2.5 rounded-xl text-sm bg-white outline-none focus-visible:ring-2 focus-visible:ring-[#4060FF] resize-y"
                style={{ border: "1px solid rgba(15,18,38,0.14)" }}
                data-testid="fr-text"
              />
              <button onClick={submit} disabled={sending || !text.trim()} className="btn-primary h-10 px-5 text-sm mt-2 disabled:opacity-50" data-testid="fr-submit-btn">
                <Send size={14} /> {sending ? "Sending…" : "Send to the developer"}
              </button>
            </div>
          </div>

          <div className="border-t pt-4" style={{ borderColor: "rgba(15,18,38,0.07)" }}>
            <div className="kpi-label mb-2">Submitted ideas ({items.length})</div>
            {items.length === 0 ? (
              <div className="text-[13px] text-ink-3" data-testid="fr-empty">Nothing yet — yours will be the first.</div>
            ) : (
              <div className="space-y-2" data-testid="fr-list">
                {items.map((it) => (
                  <div key={it.id} className="rounded-xl p-3 flex items-start gap-3" style={{ background: "rgba(15,18,38,0.03)", border: "1px solid rgba(15,18,38,0.07)" }} data-testid={`fr-item-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-ink leading-relaxed whitespace-pre-wrap">{it.text}</div>
                      <div className="text-[11px] text-ink-3 font-mono mt-1">{it.by_name || it.by_email} · {(it.created_at || "").slice(0, 10)}</div>
                    </div>
                    {(user?.is_super_admin || it.by_user_id === user?.id) && (
                      <button onClick={() => remove(it.id)} className="text-ink-3 hover:text-rose-600 transition-colors shrink-0" title="Remove" data-testid={`fr-delete-${it.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
